import { useEffect, useId, useRef, useState } from "preact/hooks";
import { drawCrosshair, settings } from "../../../settings";

/** CSS px, the canvas is scaled by devicePixelRatio. */
const SIZE = 280;
const CELLS = 2;
const MAX_SPREAD = 24;
/** Canvas colors cannot use CSS variables: a bright sky-and-chrome tone and the near-black glass of the HUD plates. */
const LIGHT = "#d7e2ee";
const DARK = "#060d18";

/** Checkerboard whose corner sits under the crosshair, so the outline is judged on bright and dark at once. */
function paintBackground(ctx: CanvasRenderingContext2D): void {
  const cell = SIZE / CELLS;
  for (let row = 0; row < CELLS; row++) {
    for (let col = 0; col < CELLS; col++) {
      ctx.fillStyle = (row + col) % 2 === 0 ? LIGHT : DARK;
      ctx.fillRect(col * cell, row * cell, cell, cell);
    }
  }
}

/**
 * Live crosshair drawn by the same `drawCrosshair` the HUD uses (including the HUD scale). The
 * spread slider is local to the preview: it shows how the arms open when `dynamic` is on.
 */
export function CrosshairPreview() {
  const canvas = useRef<HTMLCanvasElement>(null);
  const spreadId = useId();
  const [spread, setSpread] = useState(0);
  const [dpr, setDpr] = useState(() => window.devicePixelRatio || 1);
  const crosshair = settings.value.crosshair;
  const scale = settings.value.hud.scale;

  // Browser zoom changes devicePixelRatio and fires resize.
  useEffect(() => {
    const onResize = () => setDpr(window.devicePixelRatio || 1);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const el = canvas.current;
    const ctx = el?.getContext("2d");
    if (!el || !ctx) return;
    const pixels = Math.round(SIZE * dpr);
    if (el.width !== pixels) {
      el.width = pixels;
      el.height = pixels;
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    paintBackground(ctx);
    drawCrosshair(ctx, crosshair, SIZE / 2, SIZE / 2, spread, scale);
  }, [crosshair, scale, spread, dpr]);

  return (
    <figure class="settings__preview">
      <span class="eyebrow" aria-hidden="true">
        Live preview
      </span>
      <canvas ref={canvas} class="settings__canvas" role="img" aria-label="Crosshair preview" />
      <figcaption class="settings__preview-caption muted num">Drawn at HUD scale {scale.toFixed(2)}x</figcaption>
      <div class="settings__spread">
        <label class="settings__spread-label" for={spreadId}>
          Spread
        </label>
        <input
          id={spreadId}
          class="slider__range"
          type="range"
          min={0}
          max={MAX_SPREAD}
          step={1}
          value={spread}
          style={{ "--pct": `${(spread / MAX_SPREAD) * 100}%` }}
          onInput={(e) => setSpread(Number(e.currentTarget.value))}
          data-nav=""
        />
        <span class="settings__spread-value num">
          <span class="settings__lcd">{spread}</span>px
        </span>
      </div>
      <p class="settings__preview-note muted">Preview only. Spread opens the crosshair while Dynamic is on.</p>
    </figure>
  );
}
